import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import useUserLookup, { isAuth0Id } from '../../hooks/useUserLookup';
import { resolveUserDisplayName } from '../../utils/assignableUsers';

export interface AssignableUser {
  id: string;
  name: string;
}

export interface AssignedToCellProps {
  /** Row identity handed back to `onAssignmentChange` (billing item id, task id…). */
  rowId: string;
  assignedTo: string | null | undefined;
  /** Options for the picker. Callers pass a role-restricted subset where the queue requires one. */
  availableUsers: AssignableUser[];
  userIdToNameMap?: Map<string, string>;
  /**
   * Receives the raw selection: `''` to unassign, `'me'` for the Assign-to-Me
   * shortcut, otherwise the chosen user's id. The caller resolves `'me'` to the
   * signed-in identity — the cell has no opinion about who "me" is.
   */
  onAssignmentChange?: (itemId: string, value: string) => void;
  /**
   * Show the "Assign to Me" shortcut. Off where the assignable set is
   * role-restricted and the signed-in user may not be in it — offering an option
   * the server will reject is worse than not offering it (GP-1337).
   */
  includeAssignToMe?: boolean;
  disabled?: boolean;
  minWidth?: number;
  /** Shown when `availableUsers` is empty, so an empty picker explains itself. */
  noOptionsText?: string;
}

const AssignedToCellInner: React.FC<AssignedToCellProps> = ({
  rowId,
  assignedTo,
  availableUsers,
  userIdToNameMap,
  onAssignmentChange,
  includeAssignToMe = true,
  disabled = false,
  minWidth = 220,
  noOptionsText,
}) => {
  const matched = assignedTo ? availableUsers.find(u => u.id === assignedTo) : null;

  // Use API lookup for Auth0 IDs not resolved by the local map
  const shouldLookup = assignedTo && !matched && !userIdToNameMap?.get(assignedTo) && isAuth0Id(assignedTo);
  const { displayName: lookedUpName } = useUserLookup(shouldLookup ? assignedTo : null);

  // Resolve display name: local map first, then API lookup, then a formatted
  // name derived from the id. Never render a raw e-mail (GP-1335) — the map only
  // covers the roles this page fetched, so an assignee outside that set used to
  // fall through as `ffarahani@nkshealth.com` and get truncated by the cell.
  // The map/e-mail/raw-id rules live in resolveUserDisplayName (GP-1294) so the
  // read-only "Sent back by" column spells names exactly the way this cell does;
  // only the API-lookup step is unique to the picker.
  const resolvedName = assignedTo
    ? userIdToNameMap?.get(assignedTo) ||
      (lookedUpName && lookedUpName !== assignedTo
        ? lookedUpName
        : resolveUserDisplayName(assignedTo, userIdToNameMap))
    : '';

  const value = assignedTo
    ? matched || { id: assignedTo, name: resolvedName }
    : null;

  return (
    <Autocomplete
      key={`${rowId}-${assignedTo || 'unassigned'}`}
      value={value}
      disabled={disabled}
      onChange={(_, newValue) => {
        if (onAssignmentChange) {
          if (!newValue) {
            onAssignmentChange(rowId, '');
          } else if (newValue.id === 'me') {
            onAssignmentChange(rowId, 'me');
          } else {
            onAssignmentChange(rowId, newValue.id);
          }
        }
      }}
      options={
        includeAssignToMe
          ? [{ id: 'me', name: '👤 Assign to Me' }, ...availableUsers]
          : availableUsers
      }
      noOptionsText={noOptionsText}
      getOptionLabel={(option) => option?.name || 'Unknown'}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.id === val.id;
      }}
      size="small"
      sx={{ minWidth }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Unassigned"
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as any;
        return (
          <li key={option.id} {...otherProps}>
            {option.name}
          </li>
        );
      }}
    />
  );
};

/**
 * Inline "Assigned To" picker shared by every queue that routes work to a user.
 *
 * Lifted out of QueueView (billing / troubleshooting DataGrid) so the Task Queue
 * renders the SAME control instead of a second copy — its `Assigned To (User)`
 * column used to be static "Unassigned" text with no picker at all (GP-1337).
 *
 * Memoized: a full Autocomplete mounts per row, so skipping re-renders when the
 * row's assignment and the user list are unchanged saves real work on every
 * parent state change (search keystrokes, dialog opens, selection).
 */
export const AssignedToCell = React.memo(AssignedToCellInner);

export default AssignedToCell;
